import { publicSupportUnavailableReceiptSchema } from '@crm/shared/public-chat-delivery';
import { PublicSupportChatAnswerUnavailableError } from './types';
import { ApiRequestError, authApiClient } from '../auth/authClient';
import type {
  PublicSupportChatMessage,
  PublicSupportChatSession,
} from './types';
const HTTP_STATUS_TOO_MANY_REQUESTS = 429;
const HTTP_STATUS_UNAUTHORIZED = 401;
const HTTP_STATUS_FORBIDDEN = 403;
const SECONDS_PER_MINUTE = 60;

async function requestJson<T>(
  path: string,
  options: RequestInit,
  errors: { fallbackMessage: string; preserveAuthorizationError?: boolean }
): Promise<T> {
  try {
    return await authApiClient.requestPublic<T>(path, options);
  } catch (error) {
    if (error instanceof ApiRequestError) {
      const unavailable = publicSupportUnavailableReceiptSchema.safeParse(
        error.body
      );
      if (unavailable.success)
        throw new PublicSupportChatAnswerUnavailableError(
          unavailable.data.inbound,
          unavailable.data.canContinue
        );
    }
    if (
      errors.preserveAuthorizationError &&
      isPublicSupportChatSessionRejected(error)
    )
      throw error;
    throw new Error(errors.fallbackMessage);
  }
}

export async function createPublicSupportChatSession(): Promise<PublicSupportChatSession> {
  try {
    return await authApiClient.requestPublic<PublicSupportChatSession>(
      '/api/public/support/chat/session',
      { method: 'POST', body: '{}' }
    );
  } catch (error) {
    if (
      error instanceof ApiRequestError &&
      error.status === HTTP_STATUS_TOO_MANY_REQUESTS
    ) {
      const payload =
        error.body && typeof error.body === 'object'
          ? (error.body as { retryAfterSeconds?: unknown })
          : null;
      if (
        typeof payload?.retryAfterSeconds === 'number' &&
        Number.isInteger(payload.retryAfterSeconds) &&
        payload.retryAfterSeconds > 0
      ) {
        throw new PublicSupportChatSessionRateLimitError(
          payload.retryAfterSeconds
        );
      }
    }
    throw new Error('El chat no está disponible en este momento.');
  }
}

export class PublicSupportChatSessionRateLimitError extends Error {
  public readonly retryAfterSeconds: number;

  constructor(retryAfterSeconds: number) {
    const retryAfterMinutes = Math.ceil(retryAfterSeconds / SECONDS_PER_MINUTE);
    super(
      `Se alcanzó el límite de nuevas conversaciones. Intenta nuevamente en ${retryAfterMinutes} minutos.`
    );
    this.name = 'PublicSupportChatSessionRateLimitError';
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

export function getPublicSupportChatRetryAfterSeconds(
  error: unknown
): number | null {
  return error instanceof PublicSupportChatSessionRateLimitError
    ? error.retryAfterSeconds
    : null;
}

export async function getPublicSupportChatMessages(
  sessionToken: string
): Promise<PublicSupportChatMessage[]> {
  const response = await requestJson<{ messages: PublicSupportChatMessage[] }>(
    '/api/public/support/chat/messages',
    { headers: { Authorization: `Bearer ${sessionToken}` } },
    {
      fallbackMessage: 'No pudimos recuperar esta conversación.',
      preserveAuthorizationError: true,
    }
  );
  return response.messages;
}

export async function acknowledgePublicSupportChatMessages(input: {
  sessionToken: string;
  messageIds: string[];
}): Promise<number> {
  const response = await requestJson<{ acknowledged: number }>(
    '/api/public/support/chat/messages/delivered',
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${input.sessionToken}` },
      body: JSON.stringify({ messageIds: input.messageIds }),
    },
    { fallbackMessage: 'No pudimos confirmar la entrega del mensaje.' }
  );
  return response.acknowledged;
}

export function isPublicSupportChatSessionRejected(error: unknown): boolean {
  return (
    error instanceof ApiRequestError &&
    (error.status === HTTP_STATUS_UNAUTHORIZED ||
      error.status === HTTP_STATUS_FORBIDDEN)
  );
}

export async function sendPublicSupportChatMessage(input: {
  sessionToken: string;
  clientMessageId: string;
  text: string;
  pagePath: string | null;
}): Promise<{
  inbound: PublicSupportChatMessage;
  outbound: PublicSupportChatMessage | null;
  outcome?: 'review_pending';
}> {
  return requestJson<{
    inbound: PublicSupportChatMessage;
    outbound: PublicSupportChatMessage | null;
    outcome?: 'review_pending';
  }>(
    '/api/public/support/chat/messages',
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${input.sessionToken}` },
      body: JSON.stringify({
        clientMessageId: input.clientMessageId,
        text: input.text,
        pagePath: input.pagePath,
      }),
    },
    {
      fallbackMessage: 'No pudimos enviar el mensaje. Intenta nuevamente.',
      preserveAuthorizationError: true,
    }
  );
}
